<template>
  <panel-layout
    id="cancellationReason-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="cancellationReason" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ cancellationReason.isNewRecord ? 'New cancellation reason' : cancellationReason.name }}
          </h2>
          <div v-if="!cancellationReason.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.cancellationReason.edit.id', { id: cancellationReason.idReservationCancellationType })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="cancellationReason" ref="form" class="h-full w-full">
      <div class="w-full px-6 py-4">
        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')" :info="$t('pages.cancellationReason.edit.name')">
            <text-field v-model="cancellationReason.name" :label="$t('general.title')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.shortName')" :info="$t('pages.cancellationReason.edit.shortNameInfo')">
            <text-field v-model="cancellationReason.shortName" :label="$t('general.shortName')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('cancellationReason.displayMode')" :info="$t('pages.cancellationReason.edit.displayModeInfo')">
            <select-field v-model="cancellationReason.displayMode" :label="$t('cancellationReason.displayMode')" :status="status" :options="cancellationReasonDisplayModeOptions" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            col
            reverse
            class="w-full"
            :label="$t('cancellationReason.includeInHighCancellation')"
            :info="$t('pages.cancellationReason.edit.includeInHighCancellation')"
          >
            <toggle-field v-model="cancellationReason.includeInHighCancellation" :label="$t('pages.cancellationReason.edit.includeInHighCancellation')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            col
            reverse
            class="w-full"
            :label="$t('pages.cancellationReason.edit.noteRequired')"
            :info="$t('pages.cancellationReason.edit.noteRequiredInfo')"
          >
            <toggle-field v-model="cancellationReason.noteRequired" :label="$t('pages.cancellationReason.edit.noteRequired')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            col
            reverse
            class="w-full"
            :label="$t('general.enabled')"
            :info="$t('pages.cancellationReason.edit.enabledInfo')"
          >
            <toggle-field v-model="cancellationReason.enabled" :label="$t('general.enabled')" :status="status" :value="1" :uncheckedValue="0" />
          </o-field-group>
        </div>
      </div>
    </o-form>

    <template #footer>
      <div v-if="cancellationReason">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import { TextField, SelectField, ToggleField } from '@/components/fields'

import {
  ReservationCancellationTypeDisplayMode,
  ReservationCancellationTypeDisplayModeLabels,
} from '~/_api/models/reservationCancellationType'

import { usePanel } from '~/composables/usePanel'

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const props = defineProps<{ id?: string }>()

const panel = usePanel()
const confirmDialog = ref<any>(null)
const form = ref<any>()

const cancellationReason = ref<any>(null)
const errors = ref(false)
const loading = ref(true)

const { unsavedChanges, snapshot } = useUnsavedChanges(cancellationReason)

const cancellationReasonDisplayModeOptions = computed(() => {
  return Object.values(ReservationCancellationTypeDisplayMode)
    .filter((value): value is ReservationCancellationTypeDisplayMode => typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(ReservationCancellationTypeDisplayModeLabels[value]),
        value,
      }
    })
})

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      cancellationReason.value = (await $api.reservationCancellationType.defaults({} as any)).data
    } else {
      cancellationReason.value = (await $api.reservationCancellationType.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

async function save (close = false) {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const _cancellationReason = (await $api.reservationCancellationType.save(cancellationReason.value)).data

    if (_cancellationReason) {
      cancellationReason.value = _cancellationReason
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.cancellationReason.edit.success'),
        text: $i18n.t('pages.cancellationReason.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ReservationCancellationType', _cancellationReason)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

function cancel (force = false) {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

defineExpose({ save, cancel, unsavedChanges })
</script>
